<template>
    <page :style="{ height: 'unset' }" :tabs="tabs">
        <b-container fluid v-if="activeTab === 'form'">
            <busy-modal :busy="busy" />
            <b-alert v-if="isReadOnly" class="m-2" show variant="info">
                <p>
                    This data cannot be edited as the form has changed. Would you like to load the new form? Data will
                    have to be manually re-entered.
                </p>

                <b-button @click="$bvModal.show(updateDataModalId)" variant="primary">Update Data</b-button>
            </b-alert>
            <form-generator
                :key="formKey"
                :isReadOnly="isReadOnly"
                :results="surveyResults"
                :surveyJSON="surveyJSON"
                :onCompleted="surveyComplete"
            />

            <update-form-modal
                :modalId="updateDataModalId"
                @onUpdateData="onUpdateData"
                :currentSurveyJSON="currentSurveyJSON"
            />
        </b-container>
        <b-container fluid v-if="activeTab === 'history'">
            <grid :columnDefs="columnDefs" :isAutoHeight="true" :rowData="rowData"> </grid>
        </b-container>
    </page>
</template>

<script>
    import { defineAsyncComponent, toRaw } from 'vue';
    import { mapGetters } from 'vuex';
    import API from '../../../../../api.jsx';
    import busyModal from '../../../../generic/busy-modal/busy-modal.vue';
    import {
        GUEST_RESERVATION_UPDATE_VARIABLES_ENDPOINT,
        HOSTED_FORM_RESERVATION_UPDATE_FORM_DATA,
        HOSTED_FORM_RESERVATION_SUBMIT_FORM_DATA,
    } from '../../../../../endpoints';
    import FormInput from '../../../../generic/form-input/form-input.vue';
    import FormTextarea from '../../../../generic/form-textarea/form-textarea.vue';
    import FormDatepicker from '../../../../generic/form-datepicker/form-datepicker.vue';
    import { formatResponseError } from '../../../../../utils/api';
    import modal from '../../../../generic/modal/modal.vue';
    import page from '../../../../generic/page/page.vue';
    import { prepareSurveyDataToSend, prepareSurveyResults } from '../../../../hosted-form/hosted-form-page.vue';
    import ResponsiveButton from '../../../../generic/responsive-button/responsive-button.vue';
    import grid from '../../../../generic/grid/grid.vue';
    import historyActionCellRenderer from './history-action-cell-renderer.vue';
    import updateFormModal from './update-form-modal.vue';

    const FormGenerator = defineAsyncComponent(() =>
        import(
            /* webpackChunkName: "lx-form-generator" */
            '../../../../hosted-form/form-generator.vue'
        )
    );

    export default {
        name: 'GuestInvoicesCustomTab',
        components: {
            FormInput,
            FormTextarea,
            FormDatepicker,
            ResponsiveButton,
            page,
            FormGenerator,
            busyModal,
            modal,
            grid,
            updateFormModal,
            historyActionCellRenderer,
        },
        inject: ['showSuccessToast', 'showErrorToast'],
        data() {
            return {
                activeTab: 'form',
                isRendered: true,
                busy: false,
                updateDataModalId: 'update-data-modal',
                formKey: 1,
            };
        },
        computed: {
            ...mapGetters('guest', ['metadata', 'reservation']),
            ...mapGetters('header', ['userUnicodeDateFormat']),
            columnDefs() {
                return [
                    {
                        field: 'id',
                        headerName: 'ID',
                    },
                    {
                        field: 'created',
                        headerName: 'Timestamp',
                        type: 'dateTimeColumn',
                    },
                    {
                        field: '',
                        headerName: 'View',
                        cellRenderer: 'historyActionCellRenderer',
                    },
                ];
            },
            guestId() {
                return parseInt(this.$route.params.id);
            },
            reservationID() {
                return parseInt(this.$route.query.reservation);
            },
            reservationEdit() {
                return this.metadata?.permissions?.reservation_edit;
            },
            isMobile() {
                const md = 768;
                return window.innerWidth < md || window.innerHeight < md;
            },
            tabs() {
                const tabs = [];
                if (this.reservation?.invoice_variables_form?.form) {
                    tabs.push({
                        title: this.reservation.invoice_variables_form.form.title,
                        callback: () => {
                            this.activeTab = 'form';
                        },
                        active: this.activeTab == 'form',
                    });
                }
                if (this.reservation?.invoice_variables_form_logs?.length > 0) {
                    tabs.push({
                        title: 'History',
                        callback: () => {
                            this.activeTab = 'history';
                        },
                        active: this.activeTab == 'history',
                    });
                }
                return tabs;
            },
            surveyResults() {
                if (
                    this.reservation.invoice_variables_form.results &&
                    this.reservation.invoice_variables_form.results.submitted_data
                ) {
                    const surveyResults = this.reservation.invoice_variables_form.results.submitted_data;
                    const surveyObj = prepareSurveyResults(
                        surveyResults,
                        this.reservation.invoice_variables_form.results.hosted_form_json.elements
                    );
                    return surveyObj;
                }
                return {};
            },
            surveyJSON() {
                if (
                    this.reservation.invoice_variables_form.results &&
                    this.reservation.invoice_variables_form.results.hosted_form_json
                ) {
                    const json = JSON.parse(
                        JSON.stringify(this.reservation.invoice_variables_form.results.hosted_form_json)
                    );
                    json.title = ' ';
                    // json.showNavigationButtons = false;
                    return JSON.stringify(json);
                } else {
                    const json = JSON.parse(JSON.stringify(this.reservation.invoice_variables_form.form.form_json));
                    json.title = ' ';
                    return JSON.stringify(json);
                }
            },
            currentSurveyJSON() {
                if (
                    this.reservation.invoice_variables_form.form &&
                    this.reservation.invoice_variables_form.form.form_json
                ) {
                    const json = JSON.parse(JSON.stringify(this.reservation.invoice_variables_form.form.form_json));
                    json.title = ' ';
                    return JSON.stringify(json);
                }
                return '';
            },
            isReadOnly() {
                if (
                    this.reservation.invoice_variables_form.results &&
                    this.reservation.invoice_variables_form.results.hosted_form_json
                ) {
                    return (
                        JSON.stringify(this.reservation.invoice_variables_form.results.hosted_form_json) !=
                        JSON.stringify(this.reservation.invoice_variables_form.form.form_json)
                    );
                }
                return false;
            },
            rowData() {
                if (this.reservation.invoice_variables_form_logs) {
                    return this.reservation.invoice_variables_form_logs;
                }
                return [];
            },
        },
        methods: {
            refreshReservation(callback) {
                this.$emit('refreshReservations', callback);
            },
            async onUpdateData(survey) {
                try {
                    this.busy = true;
                    const data = prepareSurveyDataToSend(survey, this.currentSurveyJSON);
                    this.reservation.invoice_variables_form.results.submitted_data = data;
                    await API.post(
                        HOSTED_FORM_RESERVATION_UPDATE_FORM_DATA(this.reservation.invoice_variables_form.results.id),
                        {
                            data,
                            uuid: this.reservation.invoice_variables_form.results.uuid,
                        }
                    );
                    this.$bvModal.hide(this.updateDataModalId);
                    await this.$store.dispatch('guest/fetchReservation', {
                        reservationId: this.reservationID,
                        guestId: this.guestId,
                    });
                    this.showSuccessToast({ text: `Data successfully updated.` });
                    this.formKey += 1;
                } catch (e) {
                    this.errorToast({ text: 'Could not submit survey' });
                } finally {
                    this.busy = false;
                }
            },
            async surveyComplete(survey) {
                try {
                    this.busy = true;
                    const data = prepareSurveyDataToSend(survey, this.surveyJSON);
                    await API.post(HOSTED_FORM_RESERVATION_SUBMIT_FORM_DATA(), {
                        data,
                        uuid: this.reservation.invoice_variables_form.results?.uuid,
                        meta_reservation: this.reservation.id,
                        hosted_form: this.reservation.invoice_variables_form.form.id,
                    });
                    if (this.reservation.invoice_variables_form.results?.uuid) {
                        this.reservation.invoice_variables_form.results.submitted_data = data;
                    } else {
                        await this.$store.dispatch('guest/fetchReservation', {
                            reservationId: this.reservationID,
                            guestId: this.guestId,
                        });
                    }
                    this.formKey += 1;
                    this.successToast({ text: 'Form has been submitted.' });
                } catch (e) {
                    console.log(e);
                    this.errorToast({ text: 'Could not submit survey' });
                } finally {
                    this.busy = false;
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';
    @import '../../../../hosted-form/styles.scss';

    .loading-backdrop {
        min-height: 400px;
        width: 100%;
    }

    :deep(#surveyCreator) {
        .sv-components-row {
            background-color: #f3f3f3;
        }
    }

    @include media-breakpoint-down(md) {
        :deep(#surveyCreator) {
            .sv-components-column--expandable {
                min-height: 100%;
            }
        }
    }
</style>
